import { useState, useEffect } from "react";
import { Save, Plus, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { toast } from "sonner";

import { API } from "../../config/api";

const CONTENT_FIELDS = [
  { key: "hero_title", label: "Hero Title", type: "text" },
  { key: "hero_subtitle", label: "Hero Subtitle", type: "text" },
  { key: "hero_date", label: "Event Date", type: "text" },
  { key: "hero_location", label: "Event Location", type: "text" },
  { key: "about_text", label: "About Text", type: "textarea" },
  { key: "contact_email", label: "Contact Email", type: "email" },
  { key: "contact_phone", label: "Contact Phone", type: "tel" },
  { key: "social_facebook", label: "Facebook URL", type: "url" },
  { key: "social_instagram", label: "Instagram URL", type: "url" },
  { key: "audience_attendees", label: "Audience: Attendees", type: "text" },
  { key: "audience_community", label: "Audience: Community Size", type: "text" },
  { key: "audience_social", label: "Audience: Social Reach", type: "text" },
];

export default function AdminContent() {
  const [content, setContent] = useState({});
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    "Content-Type": "application/json"
  });

  const fetchData = async () => {
    try {
      const [contentRes, faqsRes, testimonialsRes] = await Promise.all([
        fetch(`${API}/content`),
        fetch(`${API}/admin/faqs`, { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }),
        fetch(`${API}/admin/testimonials`, { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } })
      ]);

      if (contentRes.ok) setContent(await contentRes.json());
      if (faqsRes.ok) setFaqs(await faqsRes.json());
      if (testimonialsRes.ok) setTestimonials(await testimonialsRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleContentUpdate = async (key, value) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const response = await fetch(`${API}/admin/content`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ key, value })
      });

      if (response.ok) {
        toast.success(`Updated ${key}`);
        setContent(prev => ({ ...prev, [key]: value }));
      } else {
        toast.error("Failed to update");
      }
    } catch (error) {
      toast.error("Error updating content");
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  // FAQ handlers
  const handleAddFaq = async () => {
    try {
      const response = await fetch(`${API}/admin/faqs`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ question: "Enter question here...", answer: "Enter answer here...", order_index: faqs.length })
      });

      if (response.ok) {
        toast.success("FAQ added");
        fetchData();
      }
    } catch (error) {
      toast.error("Error adding FAQ");
    }
  };

  const handleUpdateFaq = async (faq) => {
    try {
      const response = await fetch(`${API}/admin/faqs/${faq.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(faq)
      });

      if (response.ok) {
        toast.success("FAQ updated");
      }
    } catch (error) {
      toast.error("Error updating FAQ");
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!confirm("Delete this FAQ?")) return;
    try {
      const response = await fetch(`${API}/admin/faqs/${faqId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
      });

      if (response.ok) {
        toast.success("FAQ deleted");
        setFaqs(faqs.filter(f => f.id !== faqId));
      }
    } catch (error) {
      toast.error("Error deleting FAQ");
    }
  };

  // Testimonial handlers
  const handleAddTestimonial = async () => {
    try {
      const response = await fetch(`${API}/admin/testimonials`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: "Enter name...", quote: "Enter testimonial quote...", title: "Enter title/role...", order_index: testimonials.length })
      });

      if (response.ok) {
        toast.success("Testimonial added");
        fetchData();
      }
    } catch (error) {
      toast.error("Error adding testimonial");
    }
  };

  const handleUpdateTestimonial = async (testimonial) => {
    try {
      const response = await fetch(`${API}/admin/testimonials/${testimonial.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(testimonial)
      });

      if (response.ok) {
        toast.success("Testimonial updated");
      }
    } catch (error) {
      toast.error("Error updating testimonial");
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!confirm("Delete this testimonial?")) return;
    try {
      const response = await fetch(`${API}/admin/testimonials/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
      });

      if (response.ok) {
        toast.success("Testimonial deleted");
        setTestimonials(testimonials.filter(t => t.id !== id));
      }
    } catch (error) {
      toast.error("Error deleting testimonial");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-content">
      <h1 className="font-heading text-2xl text-kerala-green">Site Content</h1>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">General Content</TabsTrigger>
          <TabsTrigger value="faqs">FAQs</TabsTrigger>
          <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
        </TabsList>

        {/* General Content */}
        <TabsContent value="general" className="space-y-6">
          <div className="bg-white rounded-sm border border-kerala-gold/10 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {CONTENT_FIELDS.map(field => (
                <div key={field.key} className={field.type === "textarea" ? "md:col-span-2" : ""}>
                  <Label htmlFor={field.key}>{field.label}</Label>
                  <div className="flex gap-2 mt-1">
                    {field.type === "textarea" ? (
                      <Textarea
                        id={field.key}
                        value={content[field.key] || ""}
                        onChange={(e) => setContent({ ...content, [field.key]: e.target.value })}
                        rows={4}
                        className="flex-1"
                      />
                    ) : (
                      <Input
                        id={field.key}
                        type={field.type}
                        value={content[field.key] || ""}
                        onChange={(e) => setContent({ ...content, [field.key]: e.target.value })}
                        className="flex-1"
                      />
                    )}
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleContentUpdate(field.key, content[field.key])}
                      disabled={saving[field.key]}
                    >
                      {saving[field.key] ? (
                        <span className="animate-spin w-4 h-4 border-2 border-kerala-green border-t-transparent rounded-full" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* FAQs */}
        <TabsContent value="faqs" className="space-y-4">
          <div className="flex justify-end">
            <Button className="bg-kerala-green hover:bg-kerala-green/90" onClick={handleAddFaq}>
              <Plus className="w-4 h-4 mr-2" />
              Add FAQ
            </Button>
          </div>

          <div className="bg-white rounded-sm border border-kerala-gold/10">
            {faqs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No FAQs yet.</div>
            ) : (
              <Accordion type="single" collapsible className="p-4">
                {faqs.map((faq, idx) => (
                  <AccordionItem key={faq.id} value={faq.id}>
                    <AccordionTrigger className="text-left">
                      <span className="flex-1">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div>
                          <Label>Question</Label>
                          <Input
                            value={faq.question}
                            onChange={(e) => {
                              const updated = [...faqs];
                              updated[idx].question = e.target.value;
                              setFaqs(updated);
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Answer</Label>
                          <Textarea
                            value={faq.answer}
                            onChange={(e) => {
                              const updated = [...faqs];
                              updated[idx].answer = e.target.value;
                              setFaqs(updated);
                            }}
                            rows={3}
                            className="mt-1"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleUpdateFaq(faq)}>
                            <Save className="w-4 h-4 mr-1" />
                            Save
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteFaq(faq.id)}>
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </TabsContent>

        {/* Testimonials */}
        <TabsContent value="testimonials" className="space-y-4">
          <div className="flex justify-end">
            <Button className="bg-kerala-green hover:bg-kerala-green/90" onClick={handleAddTestimonial}>
              <Plus className="w-4 h-4 mr-2" />
              Add Testimonial
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {testimonials.map((t, idx) => (
              <div key={t.id} className="bg-white rounded-sm border border-kerala-gold/10 p-4 space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={t.name}
                    onChange={(e) => {
                      const updated = [...testimonials];
                      updated[idx].name = e.target.value;
                      setTestimonials(updated);
                    }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Title/Role</Label>
                  <Input
                    value={t.title || ""}
                    onChange={(e) => {
                      const updated = [...testimonials];
                      updated[idx].title = e.target.value;
                      setTestimonials(updated);
                    }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Quote</Label>
                  <Textarea
                    value={t.quote}
                    onChange={(e) => {
                      const updated = [...testimonials];
                      updated[idx].quote = e.target.value;
                      setTestimonials(updated);
                    }}
                    rows={3}
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleUpdateTestimonial(t)}>
                    <Save className="w-4 h-4 mr-1" />
                    Save
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDeleteTestimonial(t.id)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
